/**
 * @fileoverview Authentication validation schemas
 *
 * Schemas for user authentication including login, password reset,
 * profile updates, and user management operations.
 *
 * @module shared/schemas/auth
 */

import { z } from 'zod';

// =============================================================================
// Constants
// =============================================================================

/** Minimum password length (OWASP recommendation) */
export const MIN_PASSWORD_LENGTH = 8;

/** Maximum password length (prevents DoS via bcrypt) */
export const MAX_PASSWORD_LENGTH = 128;

/** Minimum name length */
export const MIN_NAME_LENGTH = 2;

/** Maximum name length */
export const MAX_NAME_LENGTH = 100;

// =============================================================================
// Base Schemas (Reusable Components)
// =============================================================================

/**
 * Email schema with strict validation.
 */
export const emailSchema = z
  .string()
  .email('Please enter a valid email address')
  .max(254, 'Email is too long') // RFC 5321 limit
  .toLowerCase()
  .trim();

/**
 * Password schema with security requirements.
 * Currently enforces minimum length; can be extended with complexity rules.
 */
export const passwordSchema = z
  .string()
  .min(MIN_PASSWORD_LENGTH, `Password must be at least ${MIN_PASSWORD_LENGTH} characters`)
  .max(MAX_PASSWORD_LENGTH, `Password must be ${MAX_PASSWORD_LENGTH} characters or less`);

/**
 * Name schema for user display names.
 */
export const nameSchema = z
  .string()
  .min(MIN_NAME_LENGTH, `Name must be at least ${MIN_NAME_LENGTH} characters`)
  .max(MAX_NAME_LENGTH, `Name must be ${MAX_NAME_LENGTH} characters or less`)
  .trim();

/**
 * User role schema.
 */
export const roleSchema = z.enum(['user', 'admin']).default('user');

// =============================================================================
// Authentication Schemas
// =============================================================================

/**
 * Schema for user login.
 *
 * @example
 * ```typescript
 * const credentials = loginSchema.parse({
 *   email: 'user@example.com',
 *   password: 'securepassword123',
 * });
 * ```
 */
export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Password is required'),
});

/** TypeScript type for login input */
export type LoginInput = z.infer<typeof loginSchema>;

/**
 * Schema for password reset request (forgot password).
 * Only requires email to prevent email enumeration.
 */
export const forgotPasswordSchema = z.object({
  email: emailSchema,
});

/** TypeScript type for forgot password input */
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

/**
 * Schema for resetting password with token.
 * Includes password confirmation to prevent typos.
 */
export const resetPasswordSchema = z
  .object({
    password: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

/** TypeScript type for reset password input */
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

/**
 * Schema for changing password while logged in.
 * Requires current password for security.
 */
export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })
  .refine((data) => data.currentPassword !== data.newPassword, {
    message: 'New password must be different from current password',
    path: ['newPassword'],
  });

/** TypeScript type for change password input */
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

// =============================================================================
// Profile Schemas
// =============================================================================

/**
 * Schema for updating user profile.
 */
export const updateProfileSchema = z.object({
  name: nameSchema,
});

/** TypeScript type for update profile input */
export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;

// =============================================================================
// Admin User Management Schemas
// =============================================================================

/**
 * Schema for admin creating a new user.
 */
export const createUserSchema = z.object({
  email: emailSchema,
  name: nameSchema,
  role: roleSchema,
});

/** TypeScript type for create user input */
export type CreateUserInput = z.infer<typeof createUserSchema>;

/**
 * Schema for admin updating a user.
 */
export const updateUserSchema = z.object({
  name: nameSchema.optional(),
  email: emailSchema.optional(),
  role: roleSchema.optional(),
  banned: z.boolean().optional(),
  banReason: z.string().max(500).optional().nullable(),
});

/** TypeScript type for update user input */
export type UpdateUserInput = z.infer<typeof updateUserSchema>;
